import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Tag,
  Button,
  Spin,
  Layout,
  Typography,
  Empty,
  Tooltip,
} from "antd";
import {
  ApartmentOutlined,
  CalendarOutlined,
  CheckOutlined,
  CodeOutlined,
  GlobalOutlined,
  RocketOutlined,
  ArrowLeftOutlined,
} from "@ant-design/icons";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import employeeService from "Services/AdminServices/Organization/EmployeeService";

const { Content } = Layout;
const { Text, Title } = Typography;

// Employee Dashboard mirror of the HR-side CareerPath "current trajectory"
// view (per Mudassir May-20 doc). Identical UI to the CareerPathTab embedded
// in HR's EmployeeDetail page — only the API URL differs
// (/api/CareerPath/me/career-path).
const EmployeeMyCareerPath = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [isApplicable, setIsApplicable] = useState(true);
  const [notApplicableMessage, setNotApplicableMessage] = useState("");

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        const response = await employeeService.getMyCareerPath();
        if (!mounted) return;
        setIsApplicable(response.isApplicable);
        setNotApplicableMessage(response.message || "");
        setData(response.data || null);
      } catch (error) {
        console.error("Error fetching my career path:", error);
        if (mounted) setData(null);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <Content style={{ padding: 24 }}>
          <div style={{ textAlign: "center", padding: 60 }}>
            <Spin size="large" tip="Loading your career path..." />
          </div>
        </Content>
      </DashboardLayout>
    );
  }

  if (!isApplicable) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <Content style={{ padding: 24 }}>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              padding: "80px 24px",
              maxWidth: 560,
              margin: "0 auto",
            }}
          >
            <div
              style={{
                width: 64,
                height: 64,
                borderRadius: "50%",
                background:
                  "radial-gradient(circle at center, #dbeafe 0%, #eff6ff 80%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "1.8rem",
                marginBottom: "18px",
              }}
            >
              🌟
            </div>
            <Title level={3} style={{ margin: 0, color: "#0f5a2a" }}>
              Career Path is not applicable
            </Title>
            <Text
              type="secondary"
              style={{
                fontSize: "0.95rem",
                lineHeight: 1.55,
                marginTop: "10px",
                maxWidth: 480,
              }}
            >
              {notApplicableMessage ||
                "Your role spans the organization. The standard career-path progression and alternative trajectories don't apply to org-wide leadership positions."}
            </Text>
            <Button
              icon={<ArrowLeftOutlined />}
              type="link"
              onClick={() => navigate(-1)}
              style={{ marginTop: 20 }}
            >
              Back
            </Button>
          </div>
        </Content>
      </DashboardLayout>
    );
  }

  if (!data?.currentPath) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <Content style={{ padding: 24 }}>
          <Empty description="No career path defined yet." style={{ padding: 60 }} />
        </Content>
      </DashboardLayout>
    );
  }

  const { currentPath, employee, hasAlternativeOptions } = data;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Content style={{ padding: 24, maxWidth: 1100, margin: "0 auto" }}>
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "16px 20px",
            borderRadius: 16,
            background: "linear-gradient(90deg, #f6ffed 0%, #ffffff 100%)",
            borderLeft: "5px solid #52c41a",
            boxShadow: "0 2px 12px rgba(0,0,0,0.03)",
            marginBottom: 24,
          }}
        >
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <Title level={4} style={{ margin: 0 }}>
                My Career Progression Roadmap
              </Title>
              <Tooltip
                title={
                  hasAlternativeOptions
                    ? ""
                    : "No alternative paths available yet. They appear only when your department has more than one career path configured."
                }
              >
                <span
                  style={{
                    cursor: hasAlternativeOptions ? "default" : "not-allowed",
                  }}
                >
                  <Button
                    icon={<ApartmentOutlined />}
                    type="primary"
                    style={{
                      fontSize: 12,
                      fontWeight: 500,
                      ...(hasAlternativeOptions
                        ? { background: "#0f5a2a", borderColor: "#0f5a2a" }
                        : { pointerEvents: "none" }),
                    }}
                    disabled={!hasAlternativeOptions}
                    onClick={() =>
                      navigate("/employeedashboard/career-path/alternatives")
                    }
                  >
                    See Alternative Paths
                  </Button>
                </span>
              </Tooltip>
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginTop: 4,
              }}
            >
              <CalendarOutlined
                style={{ fontSize: 12, color: "#8c8c8c", marginRight: 6 }}
              />
              <Text type="secondary" style={{ fontSize: 12 }}>
                Joined on{" "}
                <strong>
                  {employee?.hiredDate
                    ? new Date(employee.hiredDate).toLocaleDateString("en-GB", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })
                    : "N/A"}
                </strong>
              </Text>
            </div>
          </div>
          <div style={{ display: "flex", gap: 24, alignItems: "center" }}>
            <div style={{ textAlign: "right", paddingRight: 24, borderRight: "1px solid #e6ede6" }}>
              <span
                style={{
                  color: "#8c8c8c",
                  fontWeight: 600,
                  fontSize: 10,
                  letterSpacing: 0.5,
                  textTransform: "uppercase",
                  display: "block",
                }}
              >
                Prior Experience
              </span>
              <div style={{ fontSize: 22, fontWeight: 700, color: "#8c8c8c" }}>
                {(employee?.priorExperienceYears || 0).toFixed(1)}{" "}
                <small>Yrs</small>
              </div>
            </div>
            <div style={{ textAlign: "right" }}>
              <span
                style={{
                  color: "#52c41a",
                  fontWeight: 700,
                  fontSize: 11,
                  letterSpacing: 1,
                  textTransform: "uppercase",
                  display: "block",
                }}
              >
                Total Experience
              </span>
              <div style={{ fontSize: 28, fontWeight: 700, color: "#52c41a", lineHeight: 1 }}>
                {(employee?.totalExperienceYears || 0).toFixed(1)}{" "}
                <small style={{ fontSize: 16 }}>Yrs</small>
              </div>
            </div>
          </div>
        </div>

        {/* Skills + Domains */}
        <Card style={{ borderRadius: 16, marginBottom: 24 }}>
          <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 240, borderRight: "1px solid #e6ede6", paddingRight: 16 }}>
              <Text
                type="secondary"
                strong
                style={{ fontSize: 11, letterSpacing: 0.5, textTransform: "uppercase", display: "block", marginBottom: 12 }}
              >
                <CodeOutlined style={{ marginRight: 8 }} /> Your Technical Skills
              </Text>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {employee?.employeeSkills?.map((skill, idx) => (
                  <Tag key={idx} color="green" style={{ borderRadius: 12, padding: "2px 10px" }}>
                    {skill.skillName}
                  </Tag>
                )) || <Text type="secondary">No skills listed.</Text>}
              </div>
            </div>
            <div style={{ flex: 1, minWidth: 240 }}>
              <Text
                type="secondary"
                strong
                style={{ fontSize: 11, letterSpacing: 0.5, textTransform: "uppercase", display: "block", marginBottom: 12 }}
              >
                <GlobalOutlined style={{ marginRight: 8 }} /> Your Domain Expertise
              </Text>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {employee?.domainKnowledges?.map((domain, idx) => (
                  <Tag key={idx} color="gold" style={{ borderRadius: 12, padding: "2px 10px" }}>
                    {domain.name}
                  </Tag>
                )) || <Text type="secondary">No domains listed.</Text>}
              </div>
            </div>
          </div>
        </Card>

        {/* Current Path */}
        <Card style={{ borderRadius: 16 }}>
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Tag color="blue" style={{ fontSize: 10 }}>
                {currentPath.publicId}
              </Tag>
              <Title level={5} style={{ margin: 0 }}>
                {currentPath.name} Path
              </Title>
            </div>
            {currentPath.description && (
              <Text
                type="secondary"
                style={{ fontSize: 13, fontStyle: "italic", marginTop: 4, display: "block" }}
              >
                {currentPath.description}
              </Text>
            )}
          </div>

          {currentPath.domainKnowledges?.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <Text
                type="secondary"
                strong
                style={{ fontSize: 11, letterSpacing: 0.5, textTransform: "uppercase" }}
              >
                Domains in this path
              </Text>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 }}>
                {currentPath.domainKnowledges.map((d) => (
                  <Tag key={d.domainKnowledgeId} color="blue" style={{ borderRadius: 6 }}>
                    {d.name} <small>({d.industry})</small>
                  </Tag>
                ))}
              </div>
            </div>
          )}

          {/* Job title progression — Per Sara (May-25): career paths can
              BRANCH at higher steps (e.g. Lead Dev → either Project Manager
              or Senior Frontend Engineer at step 5). The old flat grid made
              parallel roles look sequential. Group by stepOrder so users
              see the actual progression structure and can identify forks. */}
          <Text
            type="secondary"
            strong
            style={{ fontSize: 11, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 12, display: "block" }}
          >
            <RocketOutlined style={{ marginRight: 6 }} /> Role Progression
          </Text>
          {(() => {
            const sortedJobs = (currentPath.jobTitles || [])
              .slice()
              .sort((a, b) => (a.stepOrder || 0) - (b.stepOrder || 0));
            // Group by stepOrder so we can render forks side-by-side.
            const grouped = sortedJobs.reduce((acc, job) => {
              const step = job.stepOrder ?? 0;
              if (!acc.has(step)) acc.set(step, []);
              acc.get(step).push(job);
              return acc;
            }, new Map());
            const steps = Array.from(grouped.entries());
            return (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "stretch",
                  gap: 8,
                }}
              >
                {steps.map(([stepOrder, jobs], idx) => {
                  const isBranching = jobs.length > 1;
                  const isLast = idx === steps.length - 1;
                  return (
                    <React.Fragment key={stepOrder}>
                      <div>
                        {isBranching && (
                          <div
                            style={{
                              textAlign: "center",
                              fontSize: 10,
                              fontWeight: 700,
                              letterSpacing: 0.6,
                              textTransform: "uppercase",
                              color: "#7c5e10",
                              background: "#fff7ed",
                              border: "1px solid #fcd34d",
                              borderRadius: 40,
                              padding: "4px 14px",
                              display: "inline-block",
                              margin: "0 auto 10px",
                              position: "relative",
                              left: "50%",
                              transform: "translateX(-50%)",
                            }}
                          >
                            🛤️ Parallel tracks at this level — choose your direction
                          </div>
                        )}
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: isBranching
                              ? `repeat(auto-fit, minmax(220px, 1fr))`
                              : "minmax(280px, 420px)",
                            gap: 12,
                            justifyContent: isBranching ? "stretch" : "center",
                          }}
                        >
                          {jobs.map((job) => {
                            const isCurrent = job.isCurrent;
                            const min = job.minExperienceRequired ?? 0;
                            const max = job.maxExperienceRequired;
                            // Defensive: bad data sometimes has max < min
                            // (e.g. min=11, max=8). Treat as open-ended.
                            const expLabel =
                              max != null && max >= min
                                ? `${min}–${max} yrs`
                                : `${min}+ yrs`;
                            return (
                              <div
                                key={job.id}
                                style={{
                                  background: isCurrent ? "#f0fdf4" : "#fff",
                                  border: `1px solid ${
                                    isCurrent ? "#22c55e" : "#e6ede6"
                                  }`,
                                  borderLeft: `4px solid ${
                                    isCurrent ? "#22c55e" : "#d1d5db"
                                  }`,
                                  borderRadius: 12,
                                  padding: 12,
                                  position: "relative",
                                }}
                              >
                                {isCurrent && (
                                  <Tag
                                    color="green"
                                    style={{
                                      position: "absolute",
                                      top: -10,
                                      right: 10,
                                      fontSize: 9,
                                      fontWeight: 700,
                                    }}
                                  >
                                    CURRENT
                                  </Tag>
                                )}
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    marginBottom: 6,
                                  }}
                                >
                                  <strong
                                    style={{
                                      fontSize: 13,
                                      color: isCurrent ? "#0f5a2a" : "#1e2f1e",
                                    }}
                                  >
                                    {job.name}
                                  </strong>
                                  <Tag style={{ fontSize: 9, margin: 0 }}>
                                    {job.publicId}
                                  </Tag>
                                </div>
                                <Text type="secondary" style={{ fontSize: 11 }}>
                                  {expLabel}
                                </Text>
                                {job.skillMatchPercentage != null && (
                                  <div
                                    style={{
                                      marginTop: 6,
                                      fontSize: 11,
                                      fontWeight: 600,
                                      color: "#0f5a2a",
                                    }}
                                  >
                                    <CheckOutlined style={{ marginRight: 4 }} />
                                    {Math.round(job.skillMatchPercentage)}% skill match
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                      {!isLast && (
                        <div
                          style={{
                            textAlign: "center",
                            color: "#94a3b8",
                            fontSize: 18,
                            lineHeight: 1,
                            padding: "2px 0",
                          }}
                          aria-hidden
                        >
                          ↓
                        </div>
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            );
          })()}
        </Card>
      </Content>
    </DashboardLayout>
  );
};

export default EmployeeMyCareerPath;
